import { AlertCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Path, PathValue, UseFormReturn } from 'react-hook-form';
import { Button } from '../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../ui/components/ui/dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../ui/components/ui/form';
import { Input } from '../../../ui/components/ui/input';
import { Switch } from '../../../ui/components/ui/switch';
import { SelfCheckoutPackageMetaResponseDto } from '../../register/checkout/step2/types/response';
import { SelfCheckoutDataSchemaType } from './forms/data-schemas/self-checkout-data-schema';

const SelfCheckoutFormItems = <T extends SelfCheckoutDataSchemaType>({
  form,
  packages
}: {
  form: UseFormReturn<T>;
  packages: SelfCheckoutPackageMetaResponseDto[];
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  const formValues = form.watch();

  // Group packages by minimumContractDurationMonths
  const groupedPackages = useMemo(() => {
    const groups: Record<number, SelfCheckoutPackageMetaResponseDto[]> = {};
    packages.forEach((pkg) => {
      const duration = pkg.minimumContractDurationMonths;
      if (!groups[duration]) {
        groups[duration] = [];
      }
      groups[duration].push(pkg);
    });
    return groups;
  }, [packages]);

  // Get duration label
  const getDurationLabel = (months: number) => {
    if (months === 1) return 'Monatlich';
    if (months === 3) return 'Quartalsweise';
    if (months === 12) return 'Jährlich';
    return `${months} Monate`;
  };

  // Validate form values against packages
  useEffect(() => {
    const currentPackageName = formValues.packageName;
    const warningsList: string[] = [];

    if (!currentPackageName || currentPackageName.trim() === '') {
      setWarnings([]);
      return;
    }

    const matchingPackages = packages.filter((pkg) => pkg.packageName === currentPackageName);

    if (matchingPackages.length === 0) {
      warningsList.push('Achtung: Der eingetragene Paketname passt nicht zu den Paketvorlagen');
    } else {
      //console.log(formValues.additionalConditions);
      //console.log(matchingPackages);
      const matchingPackage = matchingPackages.find(
        (pkg) =>
          pkg.packagePrice === formValues.packagePrice &&
          pkg.minimumContractDurationMonths === formValues.minimumContractDurationMonths &&
          pkg.capacity === formValues.allowedJobsCount &&
          pkg.additionalConditions === formValues.additionalConditions
      );

      if (!matchingPackage) {
        warningsList.push('Achtung: Die eingetragenen Paketdaten sind abweichend');
      }
    }

    setWarnings(warningsList);
  }, [
    formValues.packageName,
    formValues.packagePrice,
    formValues.minimumContractDurationMonths,
    formValues.allowedJobsCount,
    formValues.additionalConditions,
    packages
  ]);

  const handlePackageSelect = (pkg: SelfCheckoutPackageMetaResponseDto) => {
    form.setValue('packageName' as Path<T>, pkg.packageName as PathValue<T, Path<T>>);
    form.setValue('packagePrice' as Path<T>, pkg.packagePrice as PathValue<T, Path<T>>);
    form.setValue('minimumContractDurationMonths' as Path<T>, pkg.minimumContractDurationMonths as PathValue<T, Path<T>>);
    form.setValue('allowedJobsCount' as Path<T>, pkg.capacity as PathValue<T, Path<T>>);
    form.setValue('additionalConditions' as Path<T>, pkg.additionalConditions as PathValue<T, Path<T>>);
    setIsDialogOpen(false);
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        <div className="flex gap-3 items-start">
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex-shrink-0 w-full">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" className="mb-6 w-full">
                    Vorlage auswählen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Paketvorlage auswählen</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col gap-6 mt-4">
                    {Object.entries(groupedPackages)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .map(([duration, pkgs]) => (
                        <div key={duration} className="flex flex-col gap-3">
                          <h3 className="font-semibold text-lg">{getDurationLabel(parseInt(duration))}</h3>
                          <div className="grid grid-cols-1 gap-2">
                            {pkgs.map((pkg) => (
                              <button
                                key={pkg.id}
                                type="button"
                                onClick={() => handlePackageSelect(pkg)}
                                className="text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                                <div className="font-medium">{pkg.shortName}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {pkg.packagePrice.toLocaleString('de-DE')}€/Monat • Kapazität: {pkg.capacity} • Laufzeit:{' '}
                                  {pkg.minimumContractDurationMonths} Monat{pkg.minimumContractDurationMonths !== 1 ? 'e' : ''}
                                  {pkg.additionalConditions && (
                                    <div className="text-sm text-gray-600 mt-1 italic">Zusätzliche Bedingungen: {pkg.additionalConditions}</div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex gap-3">
              <div className="w-1/2">
                <FormField
                  control={form.control}
                  name={'packageName' as Path<T>}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1 bg-white rounded-lg space-y-0">
                      <FormLabel>Paketname</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Paketname" value={field.value as string} onChange={(e) => field.onChange(e.target.value)} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-1/2">
                <FormField
                  control={form.control}
                  name={'packagePrice' as Path<T>}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1 bg-white rounded-lg space-y-0">
                      <FormLabel>Paketpreis</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Paketpreis"
                          value={field.value?.toString() || ''}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-1/2">
            <FormField
              control={form.control}
              name={'minimumContractDurationMonths' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1 bg-white rounded-lg space-y-0">
                  <FormLabel>Laufzeit</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="3"
                      value={field.value?.toString() || ''}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 3)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-1/2">
            <FormField
              control={form.control}
              name={'allowedJobsCount' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1 bg-white rounded-lg space-y-0">
                  <FormLabel>Kapazität</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="1"
                      value={field.value?.toString() || ''}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 10)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-full">
            <FormField
              control={form.control}
              name={'additionalConditions' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Zusätzliche Bedingungen"
                      value={field.value as string}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        {warnings.length > 0 && (
          <div className="flex flex-col gap-1 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            {warnings.map((warning, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-yellow-800">
                <AlertCircle className="h-4 w-4" />
                <span>{warning}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <div className="w-full">
            <FormField
              control={form.control}
              name={'additionalPriceConditions' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Zusätzliche Preisbedingungen (optional)"
                      value={field.value as string}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <div className="w-full">
            <FormField
              control={form.control}
              name={'earlyOptOut' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-row h-full items-center gap-3 bg-white rounded-lg space-y-0">
                  <FormLabel>Frühzeitiger Opt-Out</FormLabel>
                  <FormControl>
                    <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfCheckoutFormItems;
